// 「最新動態」 as an event stream (W51 D-1, ADR-0160).
//
// Replaces `ModuleList.tsx`. What changed is not the styling, it is what a row
// *is*:
//
//   before — one locked row per module, each holding that module's newest item
//   after  — one row per event, across all six modules, newest write first
//
// The user reported it as 「所有的最近新增的事件都沒有正確地更新到首頁的最新動態
// 卡片」 and then said what he wanted: 「最新動態應該是所有模組的事件如果有新增
// 的話就會在這裏顯示通知」. Three diaries written this morning had nowhere to
// appear on the board, so no amount of correcting the numbers would have fixed
// it — see ADR-0160 for why the shape had to move.
//
// Four things this file is careful about:
//
// 1. **Time is relative, not a calendar day.** `formatFeedTime` (剛剛 / 3 分鐘前
//    / 3 小時前), not `formatActivityDate`. A row from two minutes ago and one
//    from six hours ago both read 「今天」 under the old formatter, so the list
//    could not be ordered or triaged by eye — which is the whole point of a feed.
// 2. **A system row shows no chip.** `actor_id` is `null` for the
//    listener-created anchor anniversary. Rendering 「我」 there would credit the
//    viewer with something they did not do (ADR-0079), so the chip is omitted
//    and the row simply reads 「紀念日 · 交往紀念日」.
// 3. **Tapping goes to the module's home page**, not to the individual item.
//    That is a deliberate scope decision recorded in spec §2.1.6: a per-item
//    detail route is a different feature, and the module home is a correct,
//    reachable destination today.
// 4. **W57.0 — the person's name is on the right, and it is their real name.**
//    The chip used to sit next to the module label on the left, reading 「我」 or
//    「對方」, and the relative time owned the right edge. Now the right edge is
//    a small rail: the name on top, the time under it, and the chevron at the
//    very edge. The name is the **display name** — 「對方」 in a column of rows
//    says nothing the reader could not already tell from the avatar, and the
//    display name says *which* person.

import { useState } from "react";
import { ChevronRight } from "lucide-react";
import { Link } from "react-router-dom";

import type { DashboardFeedItem } from "@/lib/api/dashboard";
import { DASHBOARD_I18N } from "@/lib/i18n/dashboard";
import {
  ACTIVITY_COLLAPSED_LIMIT,
  ActivityStreamCard,
} from "@/components/activity/ActivityStreamCard";
import { cn } from "@/lib/utils/cn";
import { formatFeedTime } from "@/lib/utils/date";
import { moduleDetailPath, moduleLabel } from "./moduleMeta";
import { actorName, type PairSides } from "./dashboardHelpers";

export interface ActivityFeedProps {
  items: DashboardFeedItem[];
  /** The signed-in member's id — decides whether a row reads 我 or a name. */
  currentUserId: string | null;
  /** The pair's two members, resolved **against the viewer** (W57.0). */
  sides: PairSides;
}

export function ActivityFeed({ items, currentUserId, sides }: ActivityFeedProps) {
  // W52.1 D-5 — this is the densest of the three streams (cross-module, one row
  // per event across all six) and it had **no** collapse at all, so on a busy
  // week it pushed every other card off the first screen.
  //
  // Client-side slice, because these rows arrive as props: there is no query
  // to re-run and no page size to change. The other two streams get there by
  // different routes — the wish one re-queries, the anniversary one already
  // pages — which is exactly why the shared shell takes a *count* and an
  // `expanded` flag instead of owning the data.
  const [expanded, setExpanded] = useState(false);
  const visible = expanded ? items : items.slice(0, ACTIVITY_COLLAPSED_LIMIT);

  if (items.length === 0) {
    return (
      <p
        className="text-body-sm text-ink-3"
        data-testid="activity-feed-empty"
      >
        {DASHBOARD_I18N.activity.empty}
      </p>
    );
  }

  return (
    // The card keeps the dashboard's own handle. `module-section` is what
    // `scripts/shoot-w42-live.mjs` screenshots and what `Dashboard.test.tsx`
    // asserts layout order against, so it stays on the card now that the card
    // is rendered here instead of by the caller. `activity-feed` moves down to
    // the `<ul>`, where it was before the shell existed.
    <ActivityStreamCard
      title={DASHBOARD_I18N.modules.label}
      total={items.length}
      visibleCount={items.length}
      expanded={expanded}
      onToggleExpanded={() => setExpanded((v) => !v)}
      testId="module-section"
    >
      <ul className="flex flex-col divide-y divide-hairline" data-testid="activity-feed">
        {visible.map((item) => {
        const label = moduleLabel(item.key);
        const path = moduleDetailPath(item.key);
        const actor = actorName(item.actor_id, sides, currentUserId);
        const when = formatFeedTime(item.created_at);

        const body = (
          <>
            <span className="flex-1 min-w-0">
              {/* Module label on the first line; the content the user actually
                  wrote gets the second. At 320px the sub-line's column is
                  ~43px, so `truncate` on user text is a deletion, not an
                  abbreviation — `line-clamp-2` instead. */}
              <span data-testid={`activity-label-${item.key}`} className="text-meta text-ink-3">
                {label}
              </span>
              <span className="mt-0.5 block text-body-sm line-clamp-2 text-ink-1">
                {item.text}
              </span>
            </span>
            {/* W57.0 — the right rail. The name is **plain text**, not a pill:
                a coloured pill at the right edge would become the row's second
                focal point, and the row already has a chevron there.

                W57.0 quick fix — **the two titles have to be the same type**.
                The module label is `text-meta` (12.5px / 500) and the name used
                to be `text-meta font-semibold` — the **same size**, but 600
                instead of 500. A CJK glyph drawn at 600 is visibly wider and
                heavier than the same glyph at 500, so the two titles read as
                different sizes and the row stopped looking harmonious. Measured
                in a browser: `12.5px` on both, `500` vs `600`.

                So the name now carries `text-meta` with **no** `font-semibold`
                — identical size, weight and line-height to the label on the
                other side, and the only difference is the ink step (ink-2 vs
                ink-3). That is what "aligned" means here: one scale, two
                greys.

                The relative time went back to `text-meta` for the same
                reason: `text-micro` is 11px at weight **700** with 0.08em
                tracking, and it made the right column a stack of two small
                heavy lines next to a quiet grey label. */}
            <span
              data-testid={`activity-rail-${item.key}`}
              className="flex shrink-0 flex-col items-end gap-0.5"
            >
              {actor ? (
                <span
                  data-testid={`activity-actor-${item.key}`}
                  className="max-w-[7rem] truncate text-meta text-ink-2"
                >
                  {actor}
                </span>
              ) : null}
              {when ? (
                <span className="shrink-0 text-meta text-ink-3 tabular-nums">{when}</span>
              ) : null}
            </span>
            <ChevronRight className="mt-0.5 h-4 w-4 shrink-0 text-ink-3" aria-hidden />
          </>
        );

        // W57.0 — **no `press`**. A list row is a row: iOS highlights it with a
        // background, it does not shrink it. `scale(0.97)` moved the row's
        // bottom edge up while the `divide-y` hairline stayed put, so the gap
        // between rows opened by ~3% of the row height every time one was
        // pressed. The background comes from the app-wide `:active` rule in
        // `globals.css` instead — which is also why every other list row in
        // the app now has a press state it never had on a touch device.
        const sharedClass =
          "flex w-full min-h-11 items-start gap-s-3 px-s-4 py-s-3 text-left";

        return (
          <li key={`${item.key}-${item.id ?? item.created_at}`}>
            {path ? (
              <Link
                to={path}
                data-testid={`activity-row-${item.key}`}
                aria-label={
                  actor ? `${label}：${actor} ${item.text}` : `${label}：${item.text}`
                }
                className={cn(
                  sharedClass,
                  "rounded-md hover:bg-black/5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-you-strong focus-visible:outline-offset-2",
                )}
              >
                {body}
              </Link>
            ) : (
              // A key this build does not know — render the row as static
              // content rather than a link that goes nowhere.
              <div
                data-testid={`activity-row-${item.key}`}
                className={cn(sharedClass, "rounded-md")}
              >
                {body}
              </div>
            )}
          </li>
        );
      })}
      </ul>
    </ActivityStreamCard>
  );
}
